import { parseRecordedTimestamp } from "./dateTime.js";

export const LIVE_END_GRACE_MS = 2 * 60 * 1000;

export function historyRowTime(row) {
    const parsed = parseRecordedTimestamp(row?.timestamp);
    return parsed ? parsed.getTime() : null;
}

export function chartNumericValue(value) {
    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    return null;
}

export function chartTimePoint(row) {
    const x = historyRowTime(row);
    const y = chartNumericValue(row?.value);
    if (x == null || y == null) {
        return null;
    }
    return { x, y };
}

export function chronologicalSamples(rows) {
    return [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
        const leftTime = historyRowTime(left);
        const rightTime = historyRowTime(right);
        if (leftTime == null && rightTime == null) {
            return Number(left?.id || 0) - Number(right?.id || 0);
        }
        if (leftTime == null) {
            return 1;
        }
        if (rightTime == null) {
            return -1;
        }
        if (leftTime !== rightTime) {
            return leftTime - rightTime;
        }
        return Number(left?.id || 0) - Number(right?.id || 0);
    });
}

export function liveQueryBounds(nowMs, windowMinutes, { full = true } = {}) {
    const minutes = Number(windowMinutes);
    const span = (Number.isFinite(minutes) && minutes > 0 ? minutes : 60) * 60 * 1000;
    const windowStartMs = nowMs - span;
    const windowEndMs = nowMs + LIVE_END_GRACE_MS;
    return {
        mode: Boolean(full) ? "full" : "incremental",
        queryStartMs: windowStartMs,
        queryEndMs: windowEndMs,
        windowStartMs,
        windowEndMs,
    };
}

function hasHistoryId(row) {
    return row?.id != null && row.id !== "";
}

function inWindow(row, windowStartMs, windowEndMs) {
    const time = historyRowTime(row);
    return time != null && time >= windowStartMs && time <= windowEndMs;
}

function compareSamples(left, right) {
    const leftTime = historyRowTime(left);
    const rightTime = historyRowTime(right);
    if (leftTime == null && rightTime == null) {
        return Number(left?.id || 0) - Number(right?.id || 0);
    }
    if (leftTime == null) return 1;
    if (rightTime == null) return -1;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return Number(left?.id || 0) - Number(right?.id || 0);
}

function binaryInsertionIndex(rows, row) {
    let low = 0;
    let high = rows.length;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (compareSamples(rows[middle], row) <= 0) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }
    return low;
}

function mergeIncrementalRows(existing, incoming, windowStartMs, windowEndMs) {
    const rows = chronologicalSamples(
        (Array.isArray(existing) ? existing : []).filter(
            (row) => hasHistoryId(row) && inWindow(row, windowStartMs, windowEndMs)
        )
    );
    (Array.isArray(incoming) ? incoming : []).forEach((incomingRow) => {
        if (!hasHistoryId(incomingRow)) return;
        const previousIndex = rows.findIndex((row) => row.id === incomingRow.id);
        const merged = previousIndex >= 0
            ? { ...rows[previousIndex], ...incomingRow }
            : incomingRow;
        if (previousIndex >= 0) rows.splice(previousIndex, 1);
        if (!inWindow(merged, windowStartMs, windowEndMs)) return;
        rows.splice(binaryInsertionIndex(rows, merged), 0, merged);
    });
    return rows;
}

export function reconcileLiveHistory(existing, incoming, windowStartMs, windowEndMs) {
    return mergeIncrementalRows(existing, incoming, windowStartMs, windowEndMs);
}

export function reconcileStatusEvents(existing, incoming, windowStartMs, windowEndMs) {
    return mergeIncrementalRows(existing, incoming, windowStartMs, windowEndMs);
}

export function minMaxSample(rows, maximumPoints = 1000, requiredId = null) {
    const source = Array.isArray(rows) ? rows : [];
    const limit = Math.max(4, Math.floor(Number(maximumPoints) || 1000));
    if (source.length <= limit) return source;

    const interior = source.slice(1, -1);
    const bucketCount = Math.max(1, Math.floor((limit - 2) / 2));
    const selected = [source[0]];
    for (let bucket = 0; bucket < bucketCount; bucket += 1) {
        const start = Math.floor((bucket * interior.length) / bucketCount);
        const end = Math.floor(((bucket + 1) * interior.length) / bucketCount);
        const values = interior.slice(start, end);
        if (values.length === 0) continue;
        let minimum = values[0];
        let maximum = values[0];
        values.forEach((row) => {
            const value = chartNumericValue(row?.value);
            if (value < chartNumericValue(minimum?.value)) minimum = row;
            if (value > chartNumericValue(maximum?.value)) maximum = row;
        });
        if (compareSamples(minimum, maximum) <= 0) {
            selected.push(minimum);
            if (maximum !== minimum) selected.push(maximum);
        } else {
            selected.push(maximum);
            if (maximum !== minimum) selected.push(minimum);
        }
    }
    selected.push(source[source.length - 1]);

    if (requiredId != null && !selected.some((row) => row?.id === requiredId)) {
        const required = source.find((row) => row?.id === requiredId);
        if (required) selected.push(required);
    }
    return chronologicalSamples([...new Map(selected.map((row) => [row.id, row])).values()]);
}

export function remapLiveSelection(current, nextRows) {
    if (!current) {
        return null;
    }
    const rows = Array.isArray(nextRows) ? nextRows : [];
    if (current.recordId != null) {
        const found = rows.findIndex((row) => row && row.id === current.recordId);
        return found >= 0
            ? { seriesId: current.seriesId, recordId: current.recordId, index: found }
            : null;
    }
    if (current.timestamp == null) {
        return null;
    }
    const found = rows.findIndex((row) => row && row.timestamp === current.timestamp);
    if (found < 0) {
        return null;
    }
    return { seriesId: current.seriesId, recordId: rows[found].id ?? null, index: found };
}

export function liveTimeExtent(rows) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const time = historyRowTime(row);
        if (time == null) {
            return;
        }
        if (time < min) {
            min = time;
        }
        if (time > max) {
            max = time;
        }
    });
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
    }
    const span = Math.max(max - min, 1000);
    const pad = Math.max(Math.round(span * 0.04), 1000);
    return { min: min - pad, max: max + pad, latest: max };
}

function liveEdgeTolerance(extent) {
    const span = Math.max(extent.max - extent.min, 1);
    return Math.max(span * 0.04, 1000);
}

export function rangeCoversLiveExtent(min, max, extent) {
    if (!extent || !Number.isFinite(min) || !Number.isFinite(max)) {
        return false;
    }
    const fullSpan = Math.max(extent.max - extent.min, 1);
    const tolerance = liveEdgeTolerance(extent);
    return max - min >= fullSpan * 0.98
        && min <= extent.min + tolerance
        && max >= extent.latest - tolerance;
}

export function liveZoomSource(source, min, max, extent) {
    if (source !== "user") {
        return source || "api";
    }
    return rangeCoversLiveExtent(min, max, extent) ? "api" : "user";
}

export function rememberLiveEdge(previousLatest, scaleMax, extent) {
    if (!extent) {
        return Number.isFinite(previousLatest) ? previousLatest : null;
    }
    const tolerance = liveEdgeTolerance(extent);
    if (!Number.isFinite(scaleMax) || scaleMax >= extent.latest - tolerance) {
        return extent.latest;
    }
    return Number.isFinite(previousLatest) ? previousLatest : null;
}

export function planLiveAxis({ followLatest, viewport, previousLatest, extent }) {
    if (!extent) {
        return { mode: "none" };
    }
    if (followLatest || !viewport || !Number.isFinite(viewport.min) || !Number.isFinite(viewport.max)) {
        return { mode: "fit", min: extent.min, max: extent.max, resumeFollow: false };
    }
    const fullSpan = Math.max(extent.max - extent.min, 1);
    const span = Math.max(viewport.max - viewport.min, 1);
    const tolerance = liveEdgeTolerance(extent);
    const pinned = Number.isFinite(previousLatest) && viewport.max >= previousLatest - tolerance;
    const latestMoved = Number.isFinite(previousLatest) && extent.latest > previousLatest + 1;
    if (pinned && latestMoved) {
        if (span >= fullSpan * 0.98) {
            return { mode: "fit", min: extent.min, max: extent.max, resumeFollow: true };
        }
        const nextMin = extent.max - span;
        return {
            mode: "slide",
            min: nextMin,
            max: extent.max,
            resumeFollow: false,
        };
    }
    return { mode: "preserve", min: viewport.min, max: viewport.max, resumeFollow: false };
}
